import { dateFromObjectId } from "./dateUtils.js";

/**
 * The Readlang experiments framework is inspired by Duolingo's system, except that the experiment
 * condition is determined by hashing the userID + experiment ID, allowing the client to determine
 * the condition without needing to make a request to the server.
 */

/**
 * @typedef {"freeContextTranslations2" | "reminderReprompt2" | "landingSocialProof1" | "gpt6Models1" | "dailyReminderEmail1" | "upsellRabbit1" | "stickyUpsellCta1" | "sharePrompt1" | "onboardingLibrary1"} ExperimentName
 */

/**
 * @typedef {{
 *   name: ExperimentName;
 *   conditions: string[];
 *   override?: string;
 *   userOverrides?: {[userID: string]: string};
 *   cohortStartDate?: Date;
 *   rollout?: number;
 * }} Experiment
 *
 * `rollout` is the fraction of eligible users (0..1) enrolled in the experiment;
 * omit it for a full 100% rollout. Enrolled users are split evenly across
 * `conditions`; the rest get `condition: null` (default behaviour, never
 * bucketed or treated). Enrollment uses an independent hash from condition
 * assignment, so raising `rollout` only ever *adds* users and never moves an
 * already-enrolled user's arm — a safe, monotonic ramp controlled by deploy.
 *
 * `userOverrides` pins specific users (e.g. Steve, for QA) to an arm. Like
 * `override`, the pinned user is never treated, and they bypass the cohort
 * date and rollout gates so they see the arm regardless of account age.
 */

const steveUserId = "50cb58dbf939c7397b000002";

/**
 * @type {Experiment[]}
 */
const experiments = [
  {
    // Previous experiment: daily 10-AI-translation allowance with a
    // toggle+counter strip. Stopped after D1 retention regression.
    name: "freeContextTranslations2",
    conditions: ["control", "experiment"],
    override: "control",
  },
  {
    // One-time reminder opt-in ask when the daily goal is met on the reader
    // page: the experiment arm gets the streak indicator's own popover opened
    // with a congratulation and the reminder checkbox. Both arms are bucketed
    // lazily at that goal-met moment, server-side just before the once-ever
    // claim (src/streakReminderPrompt.js). No cohort date: any account at least a day
    // old with no daily reminder qualifies. See
    // docs/experiments/2026-10-reminderReprompt2.md.
    name: "reminderReprompt2",
    conditions: ["control", "experiment"],
  },
  {
    // A social-proof line under the main landing page's hero CTA: Readlang's
    // Trustpilot rating plus "1 million+ learners since 2012". Bucketed on
    // the anonymous session's `preUserID` and rendered by the server, so the
    // arm is in the HTML with no flash; an inline beacon in htmlHeader.hbs
    // records the exposure for both arms. No cohort date: the population is
    // anonymous visitors whose ID is minted on arrival. See
    // docs/experiments/2026-10-landingSocialProof1.md.
    name: "landingSocialProof1",
    conditions: ["control", "experiment"],
  },
  {
    // The AI features on GPT-4o-mini/4o (context translations, explanations,
    // Conversation, reader review, etc.) served by GPT-6 Luna (Standard) and
    // GPT-6 Sol (Advanced), with GPT-6-specific prompts where they needed
    // them; reading guides from GPT-6 Luna. Free users' arm also gets GPT-6
    // Luna context translations instead of basic ones. Bucketed server-side
    // by `ai.useGpt6`. See docs/experiments/2026-09-gpt6Models1.md.
    name: "gpt6Models1",
    conditions: ["control", "experiment"],
    userOverrides: { [steveUserId]: "experiment" },
  },
  {
    // The personalised daily reminder email (streak progress, a puzzle word,
    // the text they're reading, flashcards due, their week and a library
    // suggestion) in place of the generic puzzle and streak emails. Bucketed
    // in the reminder cron (src/cron/sendReminderEmails.js) for both arms at
    // the point one of those emails is about to go out, so the arms differ only
    // in content. See docs/experiments/2026-10-dailyReminderEmail1.md.
    name: "dailyReminderEmail1",
    conditions: ["control", "experiment"],
    userOverrides: { [steveUserId]: "experiment" },
  },
  {
    // The premium upsell modal's rabbit in the new picture-book style (seated,
    // holding up a book) with a hand-drawn speech bubble, instead of the old
    // pop-out rabbit. Bucketed lazily when the prettier upsell modal is shown
    // to a user without Premium (premiumUpsellModal.show). No cohort date:
    // existing users seeing the new rabbit is part of the treatment. See
    // docs/experiments/2026-10-upsellRabbit1.md.
    name: "upsellRabbit1",
    conditions: ["control", "experiment"],
  },
  {
    // A sticky "See premium plans" bar at the bottom of the screen while the
    // prettier upsell modal's in-card checkout link is below the fold.
    // Bucketed lazily, in both arms, the first time that link is seen below
    // the fold for a user without Premium (premiumUpsellModal.tsx). No cohort
    // date. Independent of upsellRabbit1 (2x2). See
    // docs/experiments/2026-10-stickyUpsellCta1.md.
    name: "stickyUpsellCta1",
    conditions: ["control", "experiment"],
  },
  {
    // The website's recurring "Spread the word" timer modal (control) vs a
    // rabbit share prompt at most once a month at a delight moment: a
    // thumbs-up on the end-of-text vote, finishing a practice session, or
    // reaching a 7-day streak (shown at the next page change, never
    // mid-reading). Bucketed lazily, in both arms, for signed-in users without
    // Premium, at whichever comes first (sharePrompt.tsx). No cohort date. See
    // docs/experiments/2026-10-sharePrompt1.md.
    name: "sharePrompt1",
    conditions: ["control", "experiment"],
  },
  {
    // New accounts, and new visitors trying the website without one, start on
    // Discover under a "Start here" section (two starter stories, then
    // uploading) until they first translate, instead of on Home; Home's Get
    // Started checklist has buttons rather than instructions. On the website
    // and in the iOS app alike. Bucketed lazily at the first of: leaving
    // /start, Discover before a first translation, or Home's checklist
    // (onboarding.ts); a visitor by their session's ID, which their account
    // keeps. The cohort date keeps existing accounts out. See
    // docs/experiments/2026-10-onboardingLibrary1.md.
    //
    // Starts with the Library and Discover tabs (task 00808): no account
    // from before 2026-10-16 is in the cohort.
    name: "onboardingLibrary1",
    conditions: ["control", "experiment"],
    cohortStartDate: new Date("2026-10-16T00:00:00.000Z"),
    userOverrides: { [steveUserId]: "experiment" },
  },
];

const experimentMap = experiments.reduce(
  (map, experiment) => ({
    [experiment.name]: experiment,
    ...map,
  }),
  {}
);

export const activeExperiments = experiments.map(
  (experiment) => experiment.name
);

/**
 * Experiments still enrolling users: a global `override` means the experiment
 * has stopped and everyone gets that arm. Concluded experiments are removed
 * from the registry, or overridden while code still reads them (see
 * docs/experiments/CLAUDE.md).
 *
 * @type {ExperimentName[]}
 */
export const runningExperiments = experiments
  .filter((experiment) => !experiment.override)
  .map((experiment) => experiment.name);

/**
 * Hashes the input string, outputting a 32bit integer number.
 *
 * Adapted from https://stackoverflow.com/a/7616484
 *
 * @param {string} input
 *
 * @returns {number}
 */
const hash = function (input) {
  let hashValue = 0,
    i,
    chr;
  if (input.length === 0) return hashValue;
  for (i = 0; i < input.length; i++) {
    chr = input.charCodeAt(i);
    hashValue = (hashValue << 5) - hashValue + chr;
    hashValue |= 0; // Convert to 32bit integer
  }
  return hashValue;
};

// MurmurHash3 32-bit finalizer. Java-style String.hashCode is linear in its low
// bits, so `hash(...) % N` (especially `% 2`) leaks the parity of the input —
// causing pairs of experiments to be perfectly correlated or anti-correlated.
// fmix32 avalanches every output bit non-linearly, restoring independence.
const fmix32 = (h) => {
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h;
};

/**
 * @param {Experiment | undefined} experiment
 * @param {any} userID
 *
 * @returns {string | undefined}
 */
const getUserOverride = (experiment, userID) =>
  experiment?.userOverrides?.[String(userID)];

export const isEligibleForCohort = (userID, experimentName) => {
  const experiment = experimentMap[experimentName];
  if (!experiment?.cohortStartDate || getUserOverride(experiment, userID)) {
    return true;
  }
  if (String(userID ?? "").length !== 24) return false;
  return (
    dateFromObjectId(userID).getTime() >= experiment.cohortStartDate.getTime()
  );
};

/**
 * Stable uniform value in [0, 1) derived from a list of string parts — the
 * shared hashing primitive behind rollout gating and the lifecycle-email ghost
 * holdouts. Include a distinct salt part per use case so the namespaces stay
 * independent of condition assignment (and of each other).
 *
 * @param {string[]} parts
 *
 * @returns {number}
 */
export const getUniformValue = (parts) =>
  (Math.abs(fmix32(hash(parts.join("\n")))) % 1_000_000) / 1_000_000;

/**
 * Stable uniform value in [0, 1) for a user within an experiment, drawn from a
 * hash namespace independent of condition assignment (note the "rollout" salt),
 * so enrollment and arm are uncorrelated. Used to gate gradual rollout ramps.
 *
 * @param {any} userID
 * @param {string} experimentName
 *
 * @returns {number}
 */
export const getRolloutValue = (userID, experimentName) =>
  getUniformValue([userID, experimentName, "rollout"]);

/**
 * @param {any} userID
 * @param {ExperimentName} experimentName
 *
 * @returns {{condition: string | null, override: boolean}} condition (null when
 *   the user falls outside a partial `rollout`) and override
 */
export const getCondition = (userID, experimentName) => {
  const experiment = experimentMap[experimentName];

  if (!experiment) {
    throw Error(`Invalid experiment name: ${experimentName}`);
  }

  const override = experiment.override ?? getUserOverride(experiment, userID);
  if (override) {
    return { condition: override, override: true };
  }

  if (
    experiment.rollout != null &&
    getRolloutValue(userID, experimentName) >= experiment.rollout
  ) {
    return { condition: null, override: false };
  }

  const conditionIndex = Math.abs(
    fmix32(hash([userID, experimentName].join("\n"))) %
      experiment.conditions.length
  );

  return {
    condition: experiment.conditions[conditionIndex],
    override: false,
  };
};

export const _functionsForUnitTests = { experiments };
